import type { ClaimEdgeDeviceResult } from "@lumora/bridge";
import { useCallback, useEffect, useState } from "react";
import { SITE, client } from "../client.js";
import {
  buildClaimInput,
  claimErrorText,
  normalizeSlug,
  pickupLocationOptions,
  type LocationOption,
} from "./edgePairForm.js";

export interface EdgeDevicePairSectionProps {
  siteSlug?: string | undefined;
  /** Pre-filled from ?pair= on the dashboard deep link (#888). */
  initialCode?: string | undefined;
  /** Known slugs for multi-site owners (datalist suggestions). */
  siteOptions?: string[] | undefined;
  className?: string;
}

type PairState = "idle" | "loading" | "ready" | "error";

export function EdgeDevicePairSection({
  siteSlug,
  initialCode,
  siteOptions,
  className,
}: EdgeDevicePairSectionProps) {
  const [slugInput, setSlugInput] = useState((siteSlug ?? SITE).trim());
  const slug = normalizeSlug(slugInput);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [code, setCode] = useState((initialCode ?? "").toUpperCase());
  const [name, setName] = useState("Kitchen Pi");
  const [state, setState] = useState<PairState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClaimEdgeDeviceResult | null>(null);

  useEffect(() => {
    if (initialCode) setCode(initialCode.toUpperCase());
  }, [initialCode]);

  useEffect(() => {
    if (siteSlug) setSlugInput(siteSlug.trim());
  }, [siteSlug]);

  // Load pickup locations for the chosen slug; blank selection means the primary location.
  useEffect(() => {
    setLocationId("");
    setLocations([]);
    if (!slug) return;
    let live = true;
    void client
      .getSiteContent(slug)
      .then((c) => {
        if (live) setLocations(pickupLocationOptions(c.pickupLocations));
      })
      .catch(() => {
        if (live) setLocations([]);
      });
    return () => {
      live = false;
    };
  }, [slug]);

  const submit = useCallback(() => {
    if (!slug) {
      setState("error");
      setError("Enter a valid site slug (lowercase letters, numbers, dashes).");
      return;
    }
    const trimmed = code.replace(/\s/g, "").toUpperCase();
    if (trimmed.length !== 6) {
      setState("error");
      setError("Enter the six-character code printed on the kitchen slip.");
      return;
    }
    setState("loading");
    setError(null);
    void client
      .claimEdgeDevice(slug, buildClaimInput({ code: trimmed, name, locationId }))
      .then((res) => {
        setResult(res);
        setState("ready");
      })
      .catch((err: unknown) => {
        setState("error");
        setError(claimErrorText(err));
      });
  }, [code, name, slug, locationId]);

  return (
    <section
      className={`portal-panel edge-pair ${className ?? ""}`.trim()}
      data-lumora-view="edge-pair"
      aria-labelledby="edge-pair-heading"
    >
      <h2 id="edge-pair-heading">Pair a kitchen printer</h2>
      <p className="portal-lead" data-lumora-lead>
        When a new Raspberry Pi boots without a saved token, it prints a six-character code and a
        QR link. Enter that code here to attach the printer to the site and pickup location chosen below. The Pi
        receives its device token automatically within a few seconds.
      </p>
      <label className="edge-pair-field">
        <span>Site slug</span>
        <input
          type="text"
          name="pair-site-slug"
          list="edge-pair-sites"
          autoComplete="off"
          value={slugInput}
          onChange={(e) => setSlugInput(e.target.value)}
          aria-invalid={slug === null}
        />
        <datalist id="edge-pair-sites">
          {(siteOptions ?? []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </datalist>
      </label>
      <label className="edge-pair-field">
        <span>Pickup location</span>
        <select name="pair-location" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          <option value="">Primary location</option>
          {locations.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      </label>
      <label className="edge-pair-field">
        <span>Pairing code</span>
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          aria-invalid={state === "error"}
        />
      </label>
      <label className="edge-pair-field">
        <span>Display name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </label>
      <button type="button" className="lm-btn lm-btn-primary" disabled={state === "loading"} onClick={submit}>
        {state === "loading" ? "Pairing…" : "Pair device"}
      </button>
      {state === "error" && error ? (
        <p className="portal-error" role="alert">
          {error}
        </p>
      ) : null}
      {state === "ready" && result ? (
        <p className="portal-success" data-testid="edge-pair-success">
          Paired <strong>{result.name}</strong> ({result.deviceId}). The Pi can now poll for orders.
        </p>
      ) : null}
    </section>
  );
}
